import { computed, isRef, unref } from 'vue';
import { getMapLayers } from '@/utils/map';

export function useSelectableMapLayers(options = {}) {
    const {
        enabled = true,
        layerOptions,
        selectedLayerKey,
        resolveLayerKey = (key) => key,
        getLayerConfig = () => ({}),
        getLayers = getMapLayers,
        onLayerChange = null,
    } = options;

    const availableLayerOptions = computed(() => {
        if (unref(enabled) === false) {
            return [];
        }

        return (unref(layerOptions) ?? []).filter((option) => option?.key);
    });

    const layerEntries = computed(() => availableLayerOptions.value.flatMap((option) => {
        return getLayers(option.key, getLayerConfig(option.key, option)).map((layer) => ({
            key: option.key,
            option,
            layer,
        }));
    }));

    const mapLayers = computed(() => layerEntries.value.map((entry) => entry.layer));

    const selectedLayerIndex = computed(() => {
        const resolvedKey = resolveLayerKey(unref(selectedLayerKey));
        const index = layerEntries.value.findIndex((entry) => entry.key === resolvedKey);
        return index >= 0 ? index : 0;
    });

    const changeLayer = (key) => {
        const resolvedKey = resolveLayerKey(key);
        if (!resolvedKey) {
            return null;
        }

        if (typeof onLayerChange === 'function') {
            onLayerChange(resolvedKey);
        } else if (isRef(selectedLayerKey)) {
            selectedLayerKey.value = resolvedKey;
        }

        return resolvedKey;
    };

    const changeLayerByIndex = (index) => {
        const entry = layerEntries.value[Number(index)];
        return entry ? changeLayer(entry.key) : null;
    };

    return {
        availableLayerOptions,
        layerEntries,
        mapLayers,
        selectedLayerIndex,
        changeLayer,
        changeLayerByIndex,
    };
}
